import { Converter } from 'showdown';
import TurndownService from 'turndown';
import { gfm } from 'turndown-plugin-gfm';

// 创建转换器实例
const createConverter = () => {
    const converter = new Converter({
        tables: true, // 支持表格
        simplifiedAutoLink: true, // 支持自动链接
        excludeTrailingPunctuationFromURLs: true, // 排除URL尾部的标点符号
        strikethrough: true, // 支持删除线
        underline: true, // 支持下划线
        extensions: [
            // 处理标题
            {
                type: 'lang',
                regex: /^(#{2,4})\s+(.*?)(?:\s+\{#(.*?)\})?\s*$/gm,
                replace: (match, hashes, content, id) => {
                    const level = hashes.length;
                    return `<h${level}${id ? ` id="${id}"` : ''}>${content}</h${level}>`;
                },
            },
            // 处理摘要
            {
                type: 'lang',
                regex: /\[ABSTRACT_ZH\]([\s\S]*?)\[\/ABSTRACT_ZH\]/g,
                replace: (match, content) => {
                    // 先将内容转换为HTML
                    const htmlContent = converter.makeHtml(content);
                    return `<div id="abstract_zh" class="abstract">
                        <h2 id="abstract_zh">摘要</h2>
                        ${htmlContent}
                    </div>`;
                },
            },
            {
                type: 'lang',
                regex: /\[ABSTRACT_EN\]([\s\S]*?)\[\/ABSTRACT_EN\]/g,
                replace: (match, content) => {
                    // 先将内容转换为HTML
                    const htmlContent = converter.makeHtml(content);
                    return `<div id="abstract_en" class="abstract">
                        <h2 id="abstract_en">Abstract</h2>
                        ${htmlContent}
                    </div>`;
                },
            },
            // 处理普通文本
            {
                type: 'lang',
                regex: /\[AIML_TEXT\]([\s\S]*?)\[\/AIML_TEXT\]/g,
                replace: (match, content, id) => {
                    return `<div class="aiml-text" ${id ? `id="${id}"` : ''}>${content}</div>`;
                },
            },
            // 处理表格 - 使用闭包访问 converter
            {
                type: 'lang',
                regex: /\[AIML_TABLE id=(.*?)\]([\s\S]*?)\[\/AIML_TABLE\]/g,
                replace: (match, id, content) => {
                    // 直接使用 markdown 表格语法
                    const tableContent = content.trim();
                    if (!tableContent) return '';

                    // 确保表格内容是有效的HTML
                    let processedContent = tableContent;
                    if (!tableContent.includes('<table')) {
                        // 如果是Markdown格式，转换为HTML
                        processedContent = converter
                            .makeHtml(tableContent)
                            .replace(/<\/?table>/g, '');
                    }

                    // 清理和规范化表格内容
                    processedContent = processedContent
                        .replace(/\s+/g, ' ')
                        .replace(/>\s+</g, '><')
                        .trim();

                    return `<div class="aiml-table" data-table-id="${id}">
                        <table class="academic-table" contenteditable="false">
                            ${processedContent}
                        </table>
                    </div>`;
                },
            },
            // 处理上标
            {
                type: 'lang',
                regex: /\[AIML_SUPERSCRIPT\](.*?)\[\/AIML_SUPERSCRIPT\]/g,
                replace: (match, content) => `<sup>${content}</sup>`,
            },
            // 处理图表
            {
                type: 'lang',
                regex: /\[AIML_ECHARTS id=(.*?)\](.*?)\[\/AIML_ECHARTS\]/g,
                replace: (match, id, base64Image) =>
                    `<div class="img-container"><img class="aiml-chart" src="${base64Image}" id="${id}" alt="chart-${id}" /></div>`,
            },
            // 处理流程图
            {
                type: 'lang',
                regex: /\[AIML_MERMAID id=(.*?)\](.*?)\[\/AIML_MERMAID\]/g,
                replace: (match, id, base64Image) =>
                    `<div class="img-container"><img class="aiml-mermaid" src="${base64Image}" id="${id}" alt="mermaid-${id}" /></div>`,
            },
            // 处理Latex
            {
                type: 'lang',
                regex: /\[AIML_LATEX id=(.*?)\](.*?)\[\/AIML_LATEX\]/g,
                replace: (match, id, base64Image) =>
                    `<div class="img-container"><img class="aiml-latex" id="${id}" src="${base64Image}" alt="latex-${id}" /></div>`,
            },
        ],
    });

    return converter;
};

// 处理文档内容
export const processContent = (data) => {
    if (!data) return '';

    try {
        const converter = createConverter();
        let markdown = '';

        // 处理摘要
        if (data.abstract_zh) {
            markdown += `[ABSTRACT_ZH]${data.abstract_zh}[/ABSTRACT_ZH]\n\n`;
        }
        if (data.abstract_en) {
            markdown += `[ABSTRACT_EN]${data.abstract_en}[/ABSTRACT_EN]\n\n`;
        }

        // 处理主体内容
        if (data?.main_body?.length) {
            data.main_body.forEach((chapter) => {
                // 章节标题
                if (chapter.chapter) {
                    markdown += `## ${chapter.chapter} {#${chapter.id}}\n\n`;
                }

                // 章节内容
                if (chapter.content) {
                    markdown += processContentToMarkdown(chapter.content);
                }

                // 处理小节
                if (chapter.sections?.length) {
                    chapter.sections.forEach((section) => {
                        if (section.chapter) {
                            markdown += `### ${section.chapter} {#${section.id}}\n\n`;
                        }
                        if (section.content) {
                            markdown += processContentToMarkdown(section.content);
                        }

                        // 处理子小节
                        if (section.subSections?.length) {
                            section.subSections.forEach((subSection) => {
                                if (subSection.chapter) {
                                    markdown += `#### ${subSection.chapter} {#${subSection.id}}\n\n`;
                                }
                                if (subSection.content) {
                                    markdown += processContentToMarkdown(subSection.content);
                                }
                            });
                        }
                    });
                }
            });
        }

        return converter.makeHtml(markdown);
    } catch (error) {
        console.error('Content processing error:', error);
        return '';
    }
};

// 将内容转换为Markdown
const processContentToMarkdown = (content, sectionId) => {
    if (!content) return '';

    try {
        // 保持原有的特殊标记，让converter的extensions处理
        return content + '\n\n';
    } catch (error) {
        console.error('Content to Markdown conversion error:', error);
        return '';
    }
};

// HTML转Markdown
export const htmlToMarkdown = (html) => {
    try {
        const turndownService = new TurndownService({
            headingStyle: 'atx',
            codeBlockStyle: 'fenced',
            bulletListMarker: '-',
        });

        // 添加GFM插件支持
        turndownService.use(gfm);

        // 自定义规则 - AIML_TEXT
        turndownService.addRule('aimlText', {
            filter: (node) => {
                return node.classList.contains('aiml-text');
            },
            replacement: (content, node) => {
                return `[AIML_TEXT]${content}[/AIML_TEXT]`;
            },
        });

        // 自定义规则 - AIML_TABLE
        turndownService.addRule('aimlTable', {
            filter: (node) => {
                return node.classList.contains('aiml-table');
            },
            replacement: (content, node) => {
                const tableId = node.getAttribute('data-table-id');
                return `[AIML_TABLE id=${tableId}]${content}[/AIML_TABLE]`;
            },
        });

        // 自定义规则 - 摘要
        turndownService.addRule('abstract', {
            filter: (node) => {
                return node.classList.contains('abstract');
            },
            replacement: (content, node) => {
                const isEnglish = node.id === 'abstract_en';
                const tag = isEnglish ? 'ABSTRACT_EN' : 'ABSTRACT_ZH';
                return `[${tag}]${content}[/${tag}]`;
            },
        });

        return turndownService.turndown(html);
    } catch (error) {
        console.error('HTML to Markdown conversion error:', error);
        return '';
    }
};

// 导出一个单例转换器实例
export const converterInstance = createConverter();
